import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Avatar,
  Chip,
  Alert,
  IconButton,
  Divider,
  Stack,
  Paper,
} from '@mui/material';
import {
  Send as SendIcon,
  AutoAwesome as AIIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface Question {
  id: number;
  question: string;
  context?: string;
  category?: string;
  answered: boolean;
  answer?: string;
}

interface TranscriptMessage {
  role: 'ai' | 'client';
  message: string;
  question_id?: number;
  timestamp: string;
}

interface DiscoveryInterview {
  id: number;
  project_id: number;
  status: 'in_progress' | 'completed' | 'abandoned';
  initial_idea: string;
  questions_asked: number;
  questions_answered: number;
  progress_percentage: number;
  started_at: string;
  completed_at?: string;
}

interface DiscoveryInterviewChatProps {
  projectId: number;
  onComplete?: (interview: DiscoveryInterview) => void;
  initialIdea?: string;
}

const DiscoveryInterviewChat: React.FC<DiscoveryInterviewChatProps> = ({
  projectId,
  onComplete,
  initialIdea = '',
}) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  
  const [interview, setInterview] = useState<DiscoveryInterview | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [requestingFollowUp, setRequestingFollowUp] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const answerInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Focus input after answering a question
  useEffect(() => {
    if (!loading && answerInputRef.current) {
      answerInputRef.current.focus();
    }
  }, [loading]);

  // Start interview when component mounts if initialIdea is provided
  useEffect(() => {
    if (initialIdea && !interview) {
      startInterview(initialIdea);
    } else {
      fetchInterviewStatus();
    }
  }, []);

  const fetchInterviewStatus = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/projects/${projectId}/discovery-interview`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      if (response.data.success && response.data.data.interview) {
        const data = response.data.data;
        setInterview(data.interview);
        setQuestions(data.questions || []);
        setTranscript(data.transcript || []);
      }
    } catch (err: any) {
      // 404 is expected if no interview exists yet
      if (err.response?.status !== 404) {
        console.error('Error fetching interview status:', err);
      }
    }
  };

  const startInterview = async (idea: string) => {
    setIsStarting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/admin/projects/${projectId}/discovery-interview/start`,
        {
          initial_idea: idea,
          num_questions: 8,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setInterview(data.interview);
        setQuestions(data.questions);
        
        // Build initial transcript
        const initialTranscript: TranscriptMessage[] = [
          {
            role: 'ai',
            message: `Great! I'd like to ask you a few questions to better understand your project requirements. Let's start with the first question.`,
            timestamp: new Date().toISOString(),
          },
        ];
        setTranscript(initialTranscript);
      }
    } catch (err: any) {
      console.error('Error starting interview:', err);
      
      // If interview already exists, fetch it
      if (err.response?.data?.message?.includes('already in progress')) {
        await fetchInterviewStatus();
      } else {
        setError(err.response?.data?.message || 'Failed to start interview');
      }
    } finally {
      setIsStarting(false);
    }
  };

  const submitAnswer = async (questionId: number, answer: string, requestFollowUp: boolean = false) => {
    if (!interview || !answer.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/admin/projects/${projectId}/discovery-interview/answer`,
        {
          interview_id: interview.id,
          question_id: questionId,
          answer: answer.trim(),
          request_follow_up: requestFollowUp,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        
        // Add client answer to transcript first
        setTranscript(prev => [
          ...prev,
          {
            role: 'client',
            message: answer.trim(),
            question_id: questionId,
            timestamp: new Date().toISOString(),
          },
        ]);

        // Add appropriate AI response to transcript
        if (data.follow_up_questions && data.follow_up_questions.length > 0) {
          setTranscript(prev => [
            ...prev,
            {
              role: 'ai',
              message: `Thank you for that answer. I have a few follow-up questions based on what you've shared.`,
              timestamp: new Date().toISOString(),
            },
          ]);
        } else if (data.unanswered_questions && data.unanswered_questions.length === 0) {
          // All questions answered
          setTranscript(prev => [
            ...prev,
            {
              role: 'ai',
              message: `Excellent! You've answered all the questions. You can now complete the interview or request additional follow-up questions if needed.`,
              timestamp: new Date().toISOString(),
            },
          ]);
        } else {
          // More questions remaining
          setTranscript(prev => [
            ...prev,
            {
              role: 'ai',
              message: `Thank you! Let's move on to the next question.`,
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        setCurrentAnswer('');
        
        // Refetch the full interview to get updated questions with answered status
        await fetchInterviewStatus();
      }
    } catch (err: any) {
      console.error('Error submitting answer:', err);
      setError(err.response?.data?.message || 'Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  const requestFollowUpQuestions = async () => {
    if (!interview) return;

    setRequestingFollowUp(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/admin/projects/${projectId}/discovery-interview/generate-follow-ups`,
        {
          interview_id: interview.id,
          num_questions: 3,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setQuestions(prev => [...prev, ...data.follow_up_questions]);
        setTranscript(prev => [
          ...prev,
          {
            role: 'ai',
            message: `I've generated ${data.follow_up_questions.length} additional follow-up questions to help us understand your requirements better.`,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    } catch (err: any) {
      console.error('Error requesting follow-ups:', err);
      setError(err.response?.data?.message || 'Failed to generate follow-up questions');
    } finally {
      setRequestingFollowUp(false);
    }
  };

  const completeInterview = async () => {
    if (!interview) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/admin/projects/${projectId}/discovery-interview/complete`,
        {
          interview_id: interview.id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setInterview({ ...interview, status: 'completed', completed_at: data.completed_at });
        setTranscript(prev => [
          ...prev,
          {
            role: 'ai',
            message: `Interview completed! Your responses will be used to generate a detailed specification for your project.`,
            timestamp: new Date().toISOString(),
          },
        ]);
        
        if (onComplete) {
          onComplete({ ...interview, status: 'completed', completed_at: data.completed_at });
        }
      }
    } catch (err: any) {
      console.error('Error completing interview:', err);
      setError(err.response?.data?.message || 'Failed to complete interview');
    } finally {
      setLoading(false);
    }
  };

  const getNextUnansweredQuestion = (): Question | null => {
    return questions.find(q => !q.answered) || null;
  };

  const nextQuestion = getNextUnansweredQuestion();
  const allQuestionsAnswered = questions.length > 0 && !nextQuestion;
  const progress = interview?.progress_percentage || 0;

  if (!interview && !initialIdea) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            No Discovery Interview Started
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a new discovery interview to gather requirements from the client.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Progress */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Discovery Interview
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50 }}>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {interview?.questions_answered || 0} of {interview?.questions_asked || 0} questions answered
            </Typography>
          </Box>
          
          {interview?.status === 'completed' && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Completed"
              color="success"
              variant="filled"
            />
          )}
        </Stack>
      </Paper>

      {/* Messages Area */}
      <Card sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Stack spacing={3}>
            {/* Transcript messages */}
            {transcript.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: msg.role === 'ai' ? 'row' : 'row-reverse',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: msg.role === 'ai' ? 'primary.main' : 'secondary.main',
                    width: 40,
                    height: 40,
                  }}
                >
                  {msg.role === 'ai' ? <AIIcon /> : <PersonIcon />}
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '70%',
                    bgcolor: msg.role === 'ai' ? 'primary.50' : 'grey.100',
                  }}
                >
                  <Typography variant="body1">{msg.message}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </Typography>
                </Paper>
              </Box>
            ))}

            {/* Current question */}
            {nextQuestion && interview?.status === 'in_progress' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <HelpIcon />
                </Avatar>
                <Paper elevation={2} sx={{ p: 2, flex: 1, bgcolor: 'primary.50' }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="primary">
                      Question {questions.indexOf(nextQuestion) + 1}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {nextQuestion.question}
                    </Typography>
                    {nextQuestion.context && (
                      <Typography variant="body2" color="text.secondary">
                        {nextQuestion.context}
                      </Typography>
                    )}
                    {nextQuestion.category && (
                      <Chip
                        label={nextQuestion.category.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    )}
                  </Stack>
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Stack>
        </CardContent>

        {/* Input Area */}
        {interview?.status === 'in_progress' && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={nextQuestion ? "Type your answer here..." : "All questions answered!"}
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  disabled={loading || !nextQuestion}
                  inputRef={answerInputRef}
                  variant="outlined"
                />

                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Stack direction="row" spacing={1}>
                    {nextQuestion && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={() => submitAnswer(nextQuestion.id, currentAnswer)}
                        disabled={loading || !currentAnswer.trim()}
                      >
                        {loading ? 'Submitting...' : 'Submit Answer'}
                      </Button>
                    )}
                    
                    {allQuestionsAnswered && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={completeInterview}
                        disabled={loading}
                      >
                        Complete Interview
                      </Button>
                    )}
                  </Stack>

                  {interview.questions_answered >= 2 && (
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={requestFollowUpQuestions}
                      disabled={requestingFollowUp || loading}
                    >
                      {requestingFollowUp ? 'Generating...' : 'Request Follow-ups'}
                    </Button>
                  )}
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  💡 Tip: Provide detailed answers to help us create a better specification for your project
                </Typography>
              </Stack>
            </Box>
          </>
        )}

        {interview?.status === 'completed' && (
          <Box sx={{ p: 2, bgcolor: 'success.50' }}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="subtitle2">Interview completed successfully!</Typography>
              <Typography variant="body2">
                Your responses will be used to generate a detailed specification for your project.
              </Typography>
            </Alert>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default DiscoveryInterviewChat;
